import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  ageRating,
  getExhibitionSettings,
  getLanguage,
  getLegitimationStatus,
  getLocalizedImages,
  getOperatorsSettings,
  getProfile,
  getSelectedExhibitionId,
  getSelectedStep,
  getSelfRegistration,
  getSupportedLanguages,
  hideTopBarLogin,
  isEventSeriesPage,
  isLoggedInAsAdmin,
  isUserLoggedIn
} from '@app/app.reducer';
import { CountdownComponent } from '@app/countdown/countdown.component';
import { ResetButtonComponent } from '@app/reset-button/reset-button.component';
import { LsButtonComponent, LsDropdownComponent, LsModalComponent, LsSplitButtonComponent } from '@axess/ng-limestone';
import { BaseRoute, StepRoute } from '@core/data-access/models/routes.model';
import { RouterActions } from '@core/data-access/store/router/router.actions';
import { isRootUrl } from '@core/utils/predicates';
import { Store, select } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppConstants } from '@shared/app-constants';
import { HideTranslationPipe } from '@shared/pipes/hide-translation/hide-translation.pipe';
import { WindowSizeService } from '@shared/window-size/window-size.service';
import { GetOperatorsSettings } from '@store/customization/customization.actions';
import { ExhibitionSettingModel, OperatorsSettingsModel } from '@store/customization/customization.interfaces';
import { CustomizationService } from '@store/customization/customization.service';
import { HelperService } from '@store/helpers/helper.service';
import { LegitimationStatus } from '@store/legitimation/legitimation.interface';
import { getAllBookedProductsCount } from '@store/products/booking/product/product-booking.selectors';
import { mapStepNameToStepRoute } from '@store/step-forms/utils/step-validity-mappers';
import { UserService } from '@store/user/user.service';
import { AgeRatingComponent } from '@ui/components/age-rating/age-rating.component';
import { isEmpty } from 'lodash';
import { Observable, Subscription, combineLatest, filter, fromEvent, map, of, startWith, throttleTime } from 'rxjs';

@Component({
  selector: 'app-top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    AgeRatingComponent,
    RouterLink,
    CountdownComponent,
    ResetButtonComponent,
    RouterLinkActive,
    TranslateModule,
    HideTranslationPipe,
    LsButtonComponent,
    LsModalComponent,
    LsDropdownComponent,
    LsSplitButtonComponent
  ]
})
export class TopBarComponent implements OnInit, OnDestroy {
  readonly #destroyRef = inject(DestroyRef);
  protected readonly store = inject(Store);
  protected readonly router = inject(Router);
  protected readonly userService = inject(UserService);
  private readonly windowSizeService = inject(WindowSizeService);
  private readonly customizationService = inject(CustomizationService);
  private readonly helperService = inject(HelperService);
  private readonly translateService = inject(TranslateService);

  @Input() isBasketOpen = false;
  @Input() isMobileMenuOpen = false;

  @Output() isBasketOpenEvent = new EventEmitter<boolean>();

  isRootUrl = true;
  modalWindowOpen = false;
  isEventSelected: boolean = false;
  operatorsSettings: OperatorsSettingsModel;
  logoImage: string;
  isEventSeriesPage = false;
  displayBackToEvent = false;
  displayOnMaintenancePage = false;
  displayBackToSelfRegistration = false;
  selectedStep: string;
  isMobileResolution = false;
  exhibitionSettings: ExhibitionSettingModel;

  isSelfRegistrationEnabled = this.store.selectSignal(getSelfRegistration);
  ageRating = this.store.selectSignal(ageRating);
  isLoggedIn = this.store.selectSignal(isUserLoggedIn);
  isLoggedInAsAdmin = this.store.selectSignal(isLoggedInAsAdmin);
  profile = this.store.selectSignal(getProfile);
  legitimationStatus = this.store.selectSignal(getLegitimationStatus);
  currentLanguage = this.store.selectSignal(getLanguage);
  hideTopBarLogin = this.store.selectSignal(hideTopBarLogin);
  bookedProductsCount = this.store.selectSignal(getAllBookedProductsCount);
  supportedLanguages = this.setSupportedLanguagesSignal();
  profileMenuDropdownItems = this.setProfileMenuDropdownItemsSignal();

  readonly LegitimationStatus = LegitimationStatus;

  private getSelectedExhibitionId: number;

  private result$: Observable<Event>;

  private readonly subscriptions: Subscription = new Subscription();

  constructor() {
    // in case we are in angular universal
    if (typeof window !== 'undefined') {
      this.result$ = fromEvent(window, 'resize').pipe(throttleTime(100));
    } else {
      this.result$ = of(null);
    }

    this.subscriptions.add(
      this.router.events.subscribe((routerEvent) => {
        if (routerEvent instanceof NavigationEnd) {
          const currentUrl = this.router.url;

          this.isRootUrl = isRootUrl(currentUrl);
          this.displayBackToEvent = this.shouldDisplayBackToEvent(currentUrl);
          this.displayOnMaintenancePage = this.shouldDisplayOnMaintenancePage(currentUrl);
          this.displayBackToSelfRegistration = this.shouldDisplayBackToSelfRegistration(currentUrl);
        }
      })
    );

    this.subscriptions.add(
      this.store.pipe(select(isEventSeriesPage)).subscribe((eventSeriesPage) => {
        if (eventSeriesPage) {
          this.isEventSeriesPage = eventSeriesPage.isEventSeries;
        } else {
          this.isEventSeriesPage = false;
        }
      })
    );

    this.subscriptions.add(
      this.store.select(getSelectedStep).subscribe((selectedStep) => {
        this.selectedStep = mapStepNameToStepRoute(selectedStep);
      })
    );

    // ensure that any page with top bar has operator settings
    this.store.dispatch(new GetOperatorsSettings());

    this.subscriptions.add(
      this.store.select(getSelectedExhibitionId).subscribe((eventId) => {
        this.getSelectedExhibitionId = eventId;
        this.isEventSelected = !!eventId;
      })
    );

    this.subscriptions.add(
      this.store
        .pipe(
          select(getLocalizedImages),
          filter((images) => !!images)
        )
        .subscribe((images) => {
          this.logoImage = images.logo;
        })
    );

    this.subscriptions.add(
      this.store
        .pipe(
          select(getOperatorsSettings),
          filter((settings) => !!settings)
        )
        .subscribe((operatorsSettings) => {
          this.customizationService.setStyles();
          this.operatorsSettings = operatorsSettings;
        })
    );
  }

  ngOnInit() {
    this.isMobileMenuOpen = false;
    this.result$.pipe(startWith({})).subscribe(() => {
      this.isMobileResolution = this.windowSizeService.viewportWidth() <= AppConstants.BREAKPOINTS.md;
    });

    this.subscriptions.add(
      this.store.pipe(select(getExhibitionSettings)).subscribe((exhibitionSettings) => {
        this.exhibitionSettings = exhibitionSettings;

        this.isBasketOpenEvent.emit(this.isBasketOpen);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onToggleBasket($event: MouseEvent | KeyboardEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    this.isBasketOpenEvent.emit(!this.isBasketOpen);
  }

  onCloseBasket() {
    this.isBasketOpenEvent.emit(false);
  }

  onToggleMobileMenuButton($event: MouseEvent | KeyboardEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.isMobileMenuOpen) {
      this.isBasketOpenEvent.emit(false);
    }

    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  onCloseMobileMenu($event: MouseEvent | KeyboardEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    this.isMobileMenuOpen = false;
  }

  onChangeLanguage(selectedLanguage: string) {
    if (this.currentLanguage() !== selectedLanguage) {
      this.userService.changePreferredLanguage(selectedLanguage);
      this.helperService.setLanguage(selectedLanguage);
    }
  }

  onLoginClick() {
    this.isMobileMenuOpen = false;

    this.router.navigate([`/${BaseRoute.Login}`]);
  }

  shouldDisplayOnMaintenancePage(currentUrl: string) {
    const webShopUrlReg = `/${BaseRoute.Maintenance}`;

    if (currentUrl !== '/') {
      if (!this.isLoggedInAsAdmin()) {
        const urlMatch = currentUrl.match(webShopUrlReg);
        return urlMatch && true;
      }

      return false;
    }
  }

  shouldDisplayBackToEvent(currentUrl: string) {
    const webShopUrlReg = `/${BaseRoute.WebShop}.+`;

    if (currentUrl !== '/') {
      const urlMatch = currentUrl.match(webShopUrlReg);
      return !!urlMatch;
    }
  }

  shouldDisplayBackToSelfRegistration(currentUrl: string) {
    const webShopUrlReg = `/${BaseRoute.SelfRegistration}.+`;
    const urlMatch = !currentUrl.match(webShopUrlReg);
    return !!urlMatch;
  }

  openModalWindow() {
    this.modalWindowOpen = true;
  }

  navigateToProfile() {
    this.router.navigate([BaseRoute.Profile]);
  }

  onBackToShop() {
    this.isMobileMenuOpen = false;

    this.router.navigate([
      `${BaseRoute.WebShop}/${this.getSelectedExhibitionId}/${this.selectedStep ? this.selectedStep : StepRoute.Tickets}`
    ]);
  }

  onBackToSelfRegistration() {
    this.router.navigate([`${BaseRoute.SelfRegistration}/${this.getSelectedExhibitionId}/${this.selectedStep}`]);
  }

  protected submitModalWindow($event: Event) {
    $event.stopPropagation();

    this.isMobileMenuOpen = false;
    this.modalWindowOpen = false;

    this.store.dispatch(RouterActions.navigateToEventSelection());
  }

  protected closeModalWindow($event: Event) {
    $event.stopPropagation();

    this.modalWindowOpen = false;
  }

  protected goToSelectEvent() {
    this.store.dispatch(RouterActions.navigateToEventSelection());
  }

  protected goToUrl($event: MouseEvent, url: string) {
    $event.preventDefault();
    $event.stopPropagation();

    if (isEmpty(url)) {
      return;
    }

    window.open(url, '_blank');
  }

  private setSupportedLanguagesSignal() {
    return toSignal(
      this.store.pipe(
        select(getSupportedLanguages),
        map((languages) => languages.map((language) => ({ label: language, value: language })))
      )
    );
  }

  private setProfileMenuDropdownItemsSignal() {
    return toSignal(
      combineLatest([
        this.translateService.stream(['navigation.profile', 'navigation.logout']),
        toObservable(this.profile)
      ]).pipe(
        filter(([translations, profile]) => !!translations && !!profile),
        map(([translations]) => [
          {
            label: translations['navigation.profile'],
            icon: 'la la-user-circle',
            command: () => {
              this.navigateToProfile();
            }
          },
          {
            label: translations['navigation.logout'],
            icon: 'las la-sign-out-alt',
            command: () => {
              this.userService.logout();
            }
          }
        ]),
        takeUntilDestroyed(this.#destroyRef)
      )
    );
  }
}
